import type { Comment, CommentActor, CommentEvent } from "@malfak/contracts";
import { Archive, ArchiveRestore, CheckCheck, Eye, EyeOff, MessageSquare, Pin, PinOff, Tag, Trash2, Zap, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Badge, type Tone } from "@/components/ui/badge";
import { Tooltip } from "@/components/ui/overlays";
import { BlockedBadge, FlagChips } from "@/features/moderation/badges";
import { useMe } from "@/hooks/data";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { usePeek } from "@/lib/peek";
import { cn } from "@/lib/utils";

/**
 * What happened to a comment and who did it (ALP-84), the same everywhere a comment is shown (post page, comment panel,
 * inbox, person timeline, People, Assistant blocks, dashboards, automation log). Self-contained: give it the comment DTO.
 *
 * - `variant="compact"` (rows): at most 2 badges, by importance: Deleted › Hidden › Rule ⚡ › Replied › Author blocked.
 * - `variant="full"` (panels): a one-line status sentence, every badge and the tags.
 * Colour carries meaning only: deleted = bad, hidden = warn, rule = accent, replied = ok.
 */
export type CommentStatusInput = Pick<Comment, "status"> &
  Partial<Pick<Comment, "lastAction" | "deletedOnChannelAt" | "deletedBy" | "deletionSource" | "hiddenReason" | "privateReplySentAt" | "flags">> & {
    author?: Partial<Comment["author"]>;
  };

type Item = { key: string; tone: Tone; icon: LucideIcon; label: string; tip: string; onClick?: () => void };

/** "you" / the teammate's name / rule "Spam filter" / Instagram… */
export function useActorName() {
  const { t } = useT();
  const { data: me } = useMe();
  return (a: CommentActor | null | undefined): string => {
    if (!a) return t("commentStatus.who.teammate");
    switch (a.type) {
      case "user":
        return a.id && me?.user.id === a.id ? t("commentStatus.who.you") : a.name ? t("commentStatus.who.user", { name: a.name }) : t("commentStatus.who.teammate");
      case "rule":
        return a.name ? t("commentStatus.who.rule", { name: a.name }) : t("commentStatus.who.ruleUnnamed");
      case "agent":
        return a.name ? t("commentStatus.who.agent", { name: a.name }) : t("commentStatus.who.agentUnnamed");
      case "instagram":
        return t("commentStatus.who.instagram");
      case "author":
        return t("commentStatus.who.author");
      default:
        return t("commentStatus.who.system");
    }
  };
}

/** One sentence for an event: "Hidden by rule “Spam filter”", "Deleted by you", "Deleted on Instagram"… */
export function useEventLine() {
  const { t } = useT();
  const who = useActorName();
  return (e: CommentEvent): string => {
    if (e.kind === "deleted" && e.actor.type === "instagram") return t("commentStatus.line.deletedOnInstagram");
    if (e.kind === "deleted" && e.actor.type === "author") return t("commentStatus.line.deletedByAuthor");
    if (e.kind === "hidden" && e.actor.type === "system" && e.detail === "blocked_contact") return t("commentStatus.line.hiddenBlocked");
    if (e.kind === "tagged") return e.detail && e.actor.type !== "rule" ? t("commentStatus.line.tagged", { who: who(e.actor), detail: e.detail }) : t("commentStatus.line.taggedPlain", { who: who(e.actor) });
    if (e.kind === "rule") {
      const did = e.detail && e.detail in RULE_DID ? e.detail : "other";
      return `${who(e.actor)} · ${t(`commentStatus.ruleDid.${did as keyof typeof RULE_DID}`)}`;
    }
    return t(`commentStatus.line.${e.kind}`, { who: who(e.actor) });
  };
}

const RULE_DID = { hide: 1, delete: 1, reply: 1, reply_private: 1, flag: 1, notify: 1, other: 1 } as const;

/** The badges a comment earns, most important first. */
function useItems(c: CommentStatusInput): Item[] {
  const { t } = useT();
  const line = useEventLine();
  const { open } = usePeek();
  const last = c.lastAction ?? null;
  const when = (at: string | null | undefined) => (at ? ` · ${fmtRelative(at)}` : "");
  const items: Item[] = [];
  if (c.status === "deleted") {
    const e: CommentEvent | null = last?.kind === "deleted" ? last : c.deletedOnChannelAt && c.deletionSource !== "malfak" ? { kind: "deleted", actor: c.deletedBy === "author" ? { type: "author", id: null, name: null } : { type: "instagram", id: null, name: null }, at: c.deletedOnChannelAt } : null;
    items.push({ key: "deleted", tone: "bad", icon: Trash2, label: t("commentStatus.badge.deleted"), tip: (e ? line(e) : t("commentStatus.badge.deleted")) + when(e?.at ?? c.deletedOnChannelAt) });
  } else if (c.status === "hidden") {
    const e: CommentEvent | null = last?.kind === "hidden" ? last : c.hiddenReason === "blocked_contact" ? { kind: "hidden", actor: { type: "system", id: null, name: null }, at: "", detail: "blocked_contact" } : null;
    // PR #12: hidden because the author is blocked in Malfak reads "Hidden: blocked"
    const byBlock = c.hiddenReason === "blocked_contact" || e?.detail === "blocked_contact";
    items.push({ key: "hidden", tone: "warn", icon: EyeOff, label: byBlock ? t("people.badge.hiddenByBlock") : t("commentStatus.badge.hidden"), tip: (e ? line(e) : t("commentStatus.badge.hidden")) + when(e?.at) });
  }
  if (last?.actor.type === "rule") {
    const ruleId = last.actor.id;
    items.push({
      key: "rule",
      tone: "accent",
      icon: Zap,
      label: last.actor.name ?? t("commentStatus.badge.rule"),
      tip: `${line(last)}${when(last.at)}`,
      onClick: last.runId ? () => open("run", last.runId!) : ruleId ? () => open("rule", ruleId) : undefined,
    });
  }
  const replied = last && (last.kind === "replied" || last.kind === "private_reply") ? last : null;
  if (replied || c.privateReplySentAt) {
    const priv = replied ? replied.kind === "private_reply" : true;
    items.push({ key: "replied", tone: "ok", icon: priv ? MessageSquare : CheckCheck, label: priv ? t("commentStatus.badge.privateReply") : t("commentStatus.badge.replied"), tip: replied ? `${line(replied)}${when(replied.at)}` : `${t("commentStatus.badge.privateReply")}${when(c.privateReplySentAt)}` });
  }
  return items;
}

function StatusBadge({ item, size, interactive }: { item: Item; size: "xs" | "sm"; interactive: boolean }) {
  const Icon = item.icon;
  const badge = (
    <Badge size={size} tone={item.tone} icon={<Icon aria-hidden />}>
      <span className="max-w-[16ch] truncate" aria-hidden>
        {item.label}
      </span>
      <span className="sr-only">{item.tip}</span>
    </Badge>
  );
  // inside a clickable row: no nested controls, the tip is a native title (the panel has the details and links)
  if (!interactive)
    return (
      <span className="inline-flex" title={item.tip} data-testid={`comment-status-${item.key}`}>
        {badge}
      </span>
    );
  const body = item.onClick ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        item.onClick!();
      }}
      className="inline-flex rounded-full outline-none focus-visible:shadow-[var(--ring)]"
      data-testid={`comment-status-${item.key}`}
    >
      {badge}
    </button>
  ) : (
    <span tabIndex={0} className="inline-flex rounded-full outline-none focus-visible:shadow-[var(--ring)]" data-testid={`comment-status-${item.key}`}>
      {badge}
    </span>
  );
  return <Tooltip content={item.tip}>{body}</Tooltip>;
}

/** Badges (compact: ≤2) or the full status line + every badge + tags. Renders nothing for a plain, untouched comment. */
export function CommentStatus({
  comment: c,
  variant = "compact",
  showTags = variant === "full",
  interactive = true,
  className,
}: {
  comment: CommentStatusInput;
  variant?: "compact" | "full";
  showTags?: boolean;
  /** false inside a clickable row (a button or link): badges become plain, with a native tooltip. */
  interactive?: boolean;
  className?: string;
}) {
  const items = useItems(c);
  const blocked = !!c.author?.blocked;
  const flags = showTags ? (c.flags ?? []) : [];
  if (variant === "compact") {
    const shown = items.slice(0, blocked ? 1 : 2);
    if (!shown.length && !blocked) return null;
    return (
      <span className={cn("inline-flex flex-wrap items-center gap-1", className)} data-testid="comment-status" data-state={c.status}>
        {shown.map((i) => (
          <StatusBadge key={i.key} item={i} size="xs" interactive={interactive} />
        ))}
        {blocked && <BlockedBadge blocked />}
      </span>
    );
  }
  const head = items[0];
  if (!items.length && !blocked && !flags.length) return null;
  return (
    <div className={cn("space-y-2", className)} data-testid="comment-status" data-state={c.status}>
      {head && (
        <p className={cn("flex items-center gap-2 text-[13px] font-medium", head.tone === "bad" ? "text-bad" : head.tone === "warn" ? "text-warn" : "text-ink-2")} data-testid="comment-status-line">
          <head.icon className="size-4 shrink-0" aria-hidden />
          <span>{head.tip}</span>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {items.map((i) => (
          <StatusBadge key={i.key} item={i} size="sm" interactive={interactive} />
        ))}
        {blocked && <BlockedBadge blocked size="sm" />}
        {flags.length > 0 && <FlagChips flags={flags} max={6} size="sm" />}
      </div>
    </div>
  );
}

const EVENT_ICON: Record<CommentEvent["kind"], LucideIcon> = {
  hidden: EyeOff, unhidden: Eye, deleted: Trash2, replied: CheckCheck, private_reply: MessageSquare, tagged: Tag, untagged: Tag,
  archived: Archive, unarchived: ArchiveRestore, pinned: Pin, unpinned: PinOff, rule: Zap,
};

/** The comment panel's short history, newest first: "Hidden by rule “Spam filter” · 2 min ago". */
export function CommentHistory({ events, max = 8, action }: { events: CommentEvent[] | undefined; max?: number; action?: (e: CommentEvent, index: number) => ReactNode }) {
  const { t } = useT();
  const line = useEventLine();
  const { open } = usePeek();
  const list = (events ?? []).slice(0, max);
  if (!list.length) return <p className="text-[12.5px] text-ink-3" data-testid="comment-history-empty">{t("commentStatus.noHistory")}</p>;
  return (
    <ol className="space-y-2.5" data-testid="comment-history" aria-label={t("commentStatus.history")}>
      {list.map((e, i) => {
        const Icon = EVENT_ICON[e.kind];
        const rule = e.actor.type === "rule" && (e.runId || e.actor.id);
        return (
          <li key={`${e.kind}-${e.at}-${i}`} className="flex items-start gap-2.5" data-testid="comment-history-item" data-kind={e.kind}>
            <span className={cn("mt-0.5 grid size-6 shrink-0 place-items-center rounded-full", e.kind === "deleted" ? "bg-bad-soft text-bad" : e.kind === "hidden" ? "bg-warn-soft text-warn" : e.actor.type === "rule" ? "bg-accent-soft text-accent-ink" : "bg-surface-2 text-ink-2")} aria-hidden>
              <Icon className="size-3.5" />
            </span>
            <span className="min-w-0 flex-1 text-[12.5px] leading-snug">
              <span className="text-ink">{line(e)}</span>
              {e.at && (
                <time dateTime={e.at} title={fmtDateTime(e.at)} className="text-ink-3">
                  {" · "}
                  {fmtRelative(e.at)}
                </time>
              )}
              {rule && (
                <button type="button" onClick={() => (e.runId ? open("run", e.runId) : open("rule", e.actor.id!))} className="ms-2 text-[12px] font-medium text-accent underline-offset-2 hover:underline">
                  {e.runId ? t("commentStatus.openRun") : t("commentStatus.openRule")}
                </button>
              )}
              {action?.(e, i)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
